import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Context } from "../../components/Store";
import { useContext, useEffect, useState } from "react";
import {
  getFormattedOtherWellnessActionText,
  getWellnessActionBackgroundColor,
  getWellnessActionDisplay,
  getWellnessActionImageSrc,
  isOtherWellnessAction,
} from "@/utils/wellnessActions";
import { useBootstrap } from "@/features/bootstrap";
import wellnessPracticeProgressStore from "../../store/wellnessPracticeProgressStore";
import WellnessProgressTracker from "../../components/WellnessProgressTracker";
import { Clock } from "lucide-react";

const getTimerLengthMs = (time) => {
  if (time === "1 minute") {
    return 60000;
  }
  if (time === "2 minutes") {
    return 120000;
  }
  if (time === "30 seconds") {
    return 30000;
  }
  if (time === "20 seconds") {
    return 20000;
  }
  if (time === "10 seconds") {
    return 10000;
  }
  return 30000;
};

const PerformAction = () => {
  const navigate = useNavigate();
  const [wellnessDataPoint] = useContext(Context);
  const [progress, setProgress] = useState(0);
  const { data: user } = useBootstrap();
  const store = wellnessPracticeProgressStore;
  const time = user?.wellnesstimer ?? "30 seconds";
  const timerLength = getTimerLengthMs(time);

  useEffect(() => {
    store.setLine("line4", 0);
    setProgress(0);
  }, [store, timerLength]);

  useEffect(() => {
    store.setLine("line4", progress);
  }, [progress, store]);

  useEffect(() => {
    const progresstimer = setInterval(() => {
      setProgress((prevProgress) =>
        prevProgress >= 100 ? 100 : prevProgress + 1,
      );
    }, timerLength / 100);

    return () => {
      clearInterval(progresstimer);
    };
  }, [timerLength]);

  useEffect(() => {
    if (progress < 100) {
      return;
    }

    store.setLine("line4", 100);
    navigate("/congratulations");
  }, [navigate, progress, store]);

  const isOther = isOtherWellnessAction(wellnessDataPoint.action);
  const displayAction = getWellnessActionDisplay(wellnessDataPoint.action);
  const actionText = isOther ? "Other:" : displayAction;
  const otherText = getFormattedOtherWellnessActionText(wellnessDataPoint.action);
  const actionImageSrc = wellnessDataPoint.action
    ? getWellnessActionImageSrc(wellnessDataPoint.action, {
        adultActionImages: user?.adultActionImages,
      })
    : null;
  const enlargedPractices = {
    "Help Others": "70%",
    "Imagine a peaceful place": "68%",
    "Imagine positive things in the future": "65%",
    "Squeeze my fist": "68%",
    "Give someone a high five": "68%",
    "Write in my journal": "68%",
    "Move my body": "68%",
    "Use my growth mindset": "65%",
    "Give someone a compliment": "68%",
    Stretch: "65%",
    "Create art": "68%",
    "Be mindful. Be where my feet are": "59%",
    "Count to ten taking a deep breath each time": "52%",
  };

  const imageSize = Object.keys(enlargedPractices).includes(displayAction)
    ? enlargedPractices[displayAction]
    : "55%";

  return (
    <>
      <div className="sticky top-0 mt-10 sm:mt-0 z-10 shrink-0 bg-white sm:ml-[130px] px-0 sm:px-8 lg:px-32">
        <header className="px-4 py-4 text-center sm:px-6 sm:pb-4 sm:pt-5">
          <h1 className="mb-2 text-2xl font-bold md:text-3xl">Practice Wellness</h1>
          <p className="mx-auto text-[15px] sm:text-base text-gray-600">Take a moment to practice now</p>
        </header>
        <WellnessProgressTracker />
      </div>
      <motion.div className="flex flex-1 flex-row bg-white">
        <div className="flex w-full flex-col items-center sm:ml-[130px] sm:px-8 lg:px-32">
          <div className="mt-4 text-base font-bold text-center sm:text-lg">
          {isOther ? "" : "You Chose: "}
          </div>
          {isOther && (
            <div className="max-w-[90%] px-5 font-bold  text-center sm:text-lg">
             You chose your own wellness practice: <span className="text-emotion-green">{otherText}</span>
            </div>
          )}
          <div
            className="relative mt-4 flex h-64 w-64 flex-col items-center justify-center overflow-hidden rounded-full text-center text-white 2xl:h-96 2xl:w-96 lg:h-72 lg:w-72 sm:h-64 sm:w-64"
            style={{
              backgroundColor: getWellnessActionBackgroundColor(wellnessDataPoint.action),
            }}
          >
            {actionImageSrc && (
            <img
              className="max-h-[65%] max-w-[65%] object-contain"
              style={{ height: imageSize }}
              src={actionImageSrc}
              alt=""
            />
            )}
            <div className="max-w-[65%] px-2.5 py-2.5 text-center font-bold leading-tight line-clamp-3 sm:text-xl">
              {actionText}
            </div>
          </div>
          <div className="text-sm mt-4 opacity-75 text-[#555555BF] flex items-center gap-1"> <Clock className="w-4 h-4" /> {time}</div>
          <div className="mt-2 flex items-center justify-center gap-3" aria-label="Timer progress">
            {[0, 1, 2].map((i) => {
              const segmentStart = (i / 3) * 100;
              const segmentEnd = ((i + 1) / 3) * 100;
              const fillPercent = progress <= segmentStart ? 0 : progress >= segmentEnd ? 100 : ((progress - segmentStart) / (segmentEnd - segmentStart)) * 100;
              return (
                <div
                  key={i}
                  className="relative h-3.5 w-3.5 shrink-0 overflow-hidden rounded-full border-1 border-[#BBBBBB]"
                  aria-hidden
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-black transition-[width] duration-150 ease-linear"
                    style={{ width: `${fillPercent}%` }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default PerformAction;
